import { useRef } from "react";

import { Section, SectionHeader } from "components/landing/layout/Section";
import { Card, CardContent } from "components/products/muggleTestV0/components/ui/card";
import {
  AGENT_CAPABILITIES,
  WHY_CHOOSE_LADDER_BENEFITS,
  WHY_CHOOSE_LADDER_OUTCOME_CARDS,
  WHY_CHOOSE_LADDER_TIER1_HEADING,
  WHY_CHOOSE_LADDER_TIER2_HEADING,
  WHY_CHOOSE_LADDER_TIER3_HEADING,
  WHY_CHOOSE_MUGGLE_TEST_FEATURED_TITLE,
  WHY_CHOOSE_MUGGLE_TEST_SECTION_DESCRIPTION,
} from "content/landing";
import { useSectionViewTracking } from "lib/analytics";
import { cn } from "products/muggleTestV0/lib/utils";
import type { AgentCapability, WhyChooseLadderBenefit, WhyChooseLadderOutcomeCard } from "types/landing/landing.types";

/** Outcome row: full tile with icon (matches prior bento weight). */
const OUTCOME_CARD_CLASS =
  "group flex h-full min-h-0 flex-col border-2 border-border bg-card transition-all duration-300 hover:border-accent hover:shadow-xl";

const OUTCOME_ICON_WRAP_CLASS =
  "mb-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 transition-colors group-hover:bg-accent/20";

/** Compact tier-3 capability tile: lighter padding and smaller icon than tier-2 outcomes. */
const CAPABILITY_CARD_CLASS =
  "group flex h-full min-h-0 flex-col border border-border bg-card transition-all duration-300 hover:border-accent hover:shadow-md";

const CAPABILITY_ICON_WRAP_CLASS =
  "mb-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 transition-colors group-hover:bg-accent/20";

/**
 * Tier 1: pressure chip, connector, compact relief card.
 */
function WhyChooseLadderBenefitColumn({
  benefit,
  benefitIndex,
  isFeatured,
}: {
  benefit: WhyChooseLadderBenefit;
  benefitIndex: number;
  isFeatured: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col items-stretch animate-in fade-in-0 slide-in-from-bottom-3 duration-700 motion-reduce:animate-none",
        benefitIndex === 0 && "motion-safe:delay-75 motion-reduce:delay-0",
        benefitIndex === 1 && "motion-safe:delay-100 motion-reduce:delay-0",
        benefitIndex === 2 && "motion-safe:delay-150 motion-reduce:delay-0",
      )}
    >
      <div className="flex justify-center px-1">
        <span className="inline-flex max-w-full items-center justify-center rounded-full border border-accent/30 bg-accent/[0.08] px-3 py-1.5 text-center text-[11px] font-semibold uppercase leading-tight tracking-wide text-accent sm:px-4 sm:text-xs">
          {benefit.pressureLabel}
        </span>
      </div>
      <div
        className="mx-auto flex h-7 w-full max-w-[2rem] flex-col items-center justify-end sm:h-9"
        aria-hidden={true}
      >
        <div className="min-h-4 w-px flex-1 bg-gradient-to-b from-accent/55 via-accent/25 to-border sm:min-h-5" />
      </div>
      <Card
        className={cn(
          "border-2 border-border bg-card/90 transition-all duration-300 hover:border-accent/60 hover:shadow-md",
          isFeatured === true && "bg-accent/[0.04] ring-2 ring-accent/20",
        )}
      >
        <CardContent className="p-5 sm:p-6">
          <h4 className="text-pretty text-base font-bold text-foreground sm:text-lg">{benefit.title}</h4>
          <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
            {benefit.description}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Tier 2: pillar eyebrow + icon + title + body.
 */
function WhyChooseLadderOutcomeTile({
  outcome,
  outcomeIndex,
}: {
  outcome: WhyChooseLadderOutcomeCard;
  outcomeIndex: number;
}) {
  const Icon = outcome.icon;

  return (
    <div
      className={cn(
        "min-w-0 animate-in fade-in-0 slide-in-from-bottom-3 duration-700 motion-reduce:animate-none",
        outcomeIndex === 0 && "motion-safe:delay-200 motion-reduce:delay-0",
        outcomeIndex === 1 && "motion-safe:delay-[250ms] motion-reduce:delay-0",
        outcomeIndex === 2 && "motion-safe:delay-300 motion-reduce:delay-0",
      )}
    >
      <Card className={OUTCOME_CARD_CLASS}>
        <CardContent className="flex h-full flex-col p-6 sm:p-7">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent">{outcome.pillarLabel}</p>
          <div className={OUTCOME_ICON_WRAP_CLASS}>
            <Icon className="h-5 w-5 text-accent" />
          </div>
          <h4 className="text-pretty text-lg font-bold text-foreground">{outcome.title}</h4>
          <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground sm:text-base">
            {outcome.description}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Tier 3: compact capability tile — icon + title + body, no eyebrow.
 */
function AgentCapabilityTile({
  capability,
  capabilityIndex,
}: {
  capability: AgentCapability;
  capabilityIndex: number;
}) {
  const Icon = capability.icon;

  return (
    <div
      className={cn(
        "min-w-0 animate-in fade-in-0 slide-in-from-bottom-3 duration-700 motion-reduce:animate-none",
        capabilityIndex === 0 && "motion-safe:delay-200 motion-reduce:delay-0",
        capabilityIndex === 1 && "motion-safe:delay-[225ms] motion-reduce:delay-0",
        capabilityIndex === 2 && "motion-safe:delay-[250ms] motion-reduce:delay-0",
        capabilityIndex === 3 && "motion-safe:delay-[275ms] motion-reduce:delay-0",
        capabilityIndex === 4 && "motion-safe:delay-300 motion-reduce:delay-0",
        capabilityIndex === 5 && "motion-safe:delay-[325ms] motion-reduce:delay-0",
      )}
    >
      <Card className={CAPABILITY_CARD_CLASS}>
        <CardContent className="flex h-full flex-col p-5 sm:p-6">
          <div className={CAPABILITY_ICON_WRAP_CLASS}>
            <Icon className="h-[18px] w-[18px] text-accent" aria-hidden={true} />
          </div>
          <h4 className="text-pretty text-base font-bold text-foreground">{capability.title}</h4>
          <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground">
            {capability.description}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export function WhatYouGetSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  useSectionViewTracking("why-choose-muggle-test", sectionRef);

  return (
    <Section id="why-choose-muggle-test" className="bg-background py-4 sm:py-8 lg:py-16">
      <div ref={sectionRef} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div data-landing-scroll-anchor>
          <SectionHeader
            title="Why choose Muggle Test?"
            description={WHY_CHOOSE_MUGGLE_TEST_SECTION_DESCRIPTION}
          />
        </div>

        <div className="mt-10 space-y-10 sm:mt-12 sm:space-y-12 lg:mt-14">
          <div>
            <h3 className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground sm:mb-8 sm:text-sm sm:tracking-[0.22em]">
              {WHY_CHOOSE_LADDER_TIER1_HEADING}
            </h3>
            <div className="grid grid-cols-1 gap-8 sm:gap-6 lg:grid-cols-3 lg:gap-8">
              {WHY_CHOOSE_LADDER_BENEFITS.map((benefit, benefitIndex) => (
                <WhyChooseLadderBenefitColumn
                  key={benefit.title}
                  benefit={benefit}
                  benefitIndex={benefitIndex}
                  isFeatured={benefit.title === WHY_CHOOSE_MUGGLE_TEST_FEATURED_TITLE}
                />
              ))}
            </div>
          </div>

          <div
            className="flex items-center gap-3 sm:gap-4"
            role="separator"
            aria-orientation="horizontal"
          >
            <div className="h-px min-w-0 flex-1 bg-border" />
            <h3 className="shrink-0 text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground sm:text-sm sm:tracking-[0.22em]">
              {WHY_CHOOSE_LADDER_TIER2_HEADING}
            </h3>
            <div className="h-px min-w-0 flex-1 bg-border" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-6">
            {WHY_CHOOSE_LADDER_OUTCOME_CARDS.map((outcome, outcomeIndex) => (
              <WhyChooseLadderOutcomeTile
                key={outcome.title}
                outcome={outcome}
                outcomeIndex={outcomeIndex}
              />
            ))}
          </div>

          <div
            className="flex items-center gap-3 sm:gap-4"
            role="separator"
            aria-orientation="horizontal"
          >
            <div className="h-px min-w-0 flex-1 bg-border" />
            <h3 className="shrink-0 text-center text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground sm:text-sm sm:tracking-[0.22em]">
              {WHY_CHOOSE_LADDER_TIER3_HEADING}
            </h3>
            <div className="h-px min-w-0 flex-1 bg-border" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3 lg:gap-6">
            {AGENT_CAPABILITIES.map((capability, capabilityIndex) => (
              <AgentCapabilityTile
                key={capability.title}
                capability={capability}
                capabilityIndex={capabilityIndex}
              />
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}
